<template>
  <div class="info-center-container" ref="containerRef">
    
    <!-- Elegant Background -->
    <div class="info-center-bg">
      <div class="floating-orb orb-1"></div>
      <div class="floating-orb orb-2"></div>
      <div class="floating-orb orb-3"></div>
      <div class="gradient-mesh"></div>
      <div class="light-rays"></div>
    </div>

    <!-- Main Content -->
    <div class="info-center-content">

      <!-- Marketing Description Section -->
      <section class="marketing-section" ref="marketingSectionRef">
        <div class="marketing-container">
          <div class="marketing-badge">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 2l2 6h6l-5 4 2 6-5-4-5 4 2-6-5-4h6l2-6z" fill="currentColor"/>
            </svg>
            <span>{{ $t('infoCenter.marketing.badge') }}</span>
          </div>
          <h2 class="marketing-title">{{ $t('infoCenter.marketing.title') }}</h2>
          <p class="marketing-description">
            {{ $t('infoCenter.marketing.description') }}
          </p>
          
          <div class="features-highlight">
            <div class="feature-item" v-for="(feature, idx) in features" :key="idx">
              <div class="feature-icon">
                <v-icon :icon="feature.icon" size="24"></v-icon>
              </div>
              <div class="feature-text">
                <h3>{{ feature.title }}</h3>
                <p>{{ feature.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Hero Section with Marketing Description -->
      <section class="info-hero">
        <div class="hero-welcome">
          <v-avatar size="70" class="avatar-circle">
            <v-img :src="userAvatar" :alt="userName" cover></v-img>
          </v-avatar>
          <div class="welcome-text">
            <h1 class="welcome-title">{{ $t('infoCenter.hero.greeting') }} {{ userName }}!</h1>
            <div class="location-info">
              <span class="location-name">
                <v-icon icon="mdi-map-marker" size="16"></v-icon>
                {{ location }}
              </span>
              <span class="weather-info">
                <v-icon icon="mdi-weather-cloudy" size="16" class="weather-icon"></v-icon>
                {{ weather.temp }} {{ weather.condition }}
              </span>
              <span class="humidity">
                <v-icon icon="mdi-water" size="16"></v-icon>
                {{ weather.humidity }}
              </span>
              <span class="wind">
                <v-icon icon="mdi-weather-windy" size="16"></v-icon>
                {{ weather.wind }}
              </span>
            </div>
          </div>
        </div>
      </section>

      <div class="info-grid">
        
        <!-- News Section -->
        <section class="info-section news-section">
          <div class="section-header">
            <div class="header-content">
              <h2 class="section-title">{{ $t('infoCenter.news.title') }}</h2>
              <p class="section-subtitle">{{ $t('infoCenter.news.subtitle') }}</p>
            </div>
          </div>
          
          <div class="news-grid">
            <article 
              v-for="(item, idx) in newsItems" 
              :key="idx" 
              class="news-card"
            >
              <div class="news-image">
                <img :src="item.image" :alt="item.title" loading="lazy" />
                <div class="news-category">{{ item.category }}</div>
              </div>
              <div class="news-content">
                <div class="news-meta">
                  <span class="news-date">
                    <v-icon icon="mdi-clock-outline" size="14"></v-icon>
                    {{ item.date }}
                  </span>
                  <span class="news-author" v-if="item.author">
                    <v-icon icon="mdi-account" size="14"></v-icon>
                    {{ item.author }}
                  </span>
                </div>
                <h3 class="news-title">{{ item.title }}</h3>
                <p class="news-description">{{ item.description }}</p>
                
                <!-- File Attachments -->
                <div class="news-attachments" v-if="item.attachments && item.attachments.length > 0">
                  <div class="attachment-label">
                    <v-icon icon="mdi-file-document-outline" size="16"></v-icon>
                    {{ $t('infoCenter.news.attachments') }}:
                  </div>
                  <div class="attachment-list">
                    <a 
                      v-for="(file, fileIdx) in item.attachments" 
                      :key="fileIdx" 
                      :href="file.url" 
                      class="attachment-link"
                      target="_blank"
                      download
                    >
                      <v-icon icon="mdi-download" size="14"></v-icon>
                      {{ file.name }}
                    </a>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        <!-- Events Section -->
        <aside class="info-section events-section">
          <div class="section-header">
            <div class="header-content">
              <h2 class="section-title">{{ $t('infoCenter.events.title') }}</h2>
              <p class="section-subtitle">{{ $t('infoCenter.events.subtitle') }}</p>
            </div>
          </div>

          <div class="events-list">
            <article 
              v-for="(event, idx) in upcomingEvents" 
              :key="idx" 
              class="event-card"
            >
              <div class="event-content">
                <h3 class="event-title">{{ event.title }}</h3>
                <p class="event-date">
                  <v-icon icon="mdi-calendar" size="14"></v-icon>
                  {{ event.date }}
                </p>
                <p class="event-description">{{ event.description }}</p>
              </div>
              <div class="event-actions">
                <button 
                  @click="addToCalendar(event, 'google')" 
                  class="calendar-btn google-btn"
                  :title="$t('infoCenter.events.addToGoogle')"
                >
                  <v-icon icon="mdi-google" size="16"></v-icon>
                  Google
                </button>
                <button 
                  @click="addToCalendar(event, 'outlook')" 
                  class="calendar-btn outlook-btn"
                  :title="$t('infoCenter.events.addToOutlook')"
                >
                  <v-icon icon="mdi-microsoft-outlook" size="16"></v-icon>
                  Outlook
                </button>
                <button 
                  @click="addToCalendar(event, 'ics')" 
                  class="calendar-btn ics-btn"
                  :title="$t('infoCenter.events.downloadICS')"
                >
                  <v-icon icon="mdi-download" size="16"></v-icon>
                  .ics
                </button>
              </div>
            </article>
          </div>
        </aside>

      </div>

    </div>

  </div>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeMount, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Template refs
const containerRef = ref(null);
const marketingSectionRef = ref(null);

// User data
const userName = ref('David Miller');
const userAvatar = ref('/assets/alex_avatar.png');
const location = ref('München, DE');
const weather = ref({
  temp: '11°C',
  condition: 'overcast clouds',
  humidity: '84%',
  wind: '0.45 km/h'
});

// Helper function to get future date
const getFutureDate = (daysFromNow) => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date;
};

// Helper function to format date
const formatDate = (date) => {
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

// Helper function to get recent date (within last week)
const getRecentDate = (daysAgo) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date;
};

// Helper function to format relative date
const formatRelativeDate = (daysAgo) => {
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo} days ago`;
};

// Features data
const features = computed(() => [
  {
    icon: 'mdi-newspaper-variant-outline',
    title: t('infoCenter.marketing.features.news.title'),
    description: t('infoCenter.marketing.features.news.description')
  },
  {
    icon: 'mdi-calendar-month',
    title: t('infoCenter.marketing.features.events.title'),
    description: t('infoCenter.marketing.features.events.description')
  },
  {
    icon: 'mdi-file-document-multiple-outline',
    title: t('infoCenter.marketing.features.attachments.title'),
    description: t('infoCenter.marketing.features.attachments.description')
  },
  {
    icon: 'mdi-clock-fast',
    title: t('infoCenter.marketing.features.realtime.title'),
    description: t('infoCenter.marketing.features.realtime.description')
  }
]);

// News items with current dates (within last week) - using department names only
const newsItems = computed(() => [
  {
    title: t('infoCenter.news.items.newEmployee.title'),
    description: t('infoCenter.news.items.newEmployee.description'),
    image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.team'),
    date: formatRelativeDate(0), // Today
    author: 'HR Team',
    attachments: []
  },
  {
    title: t('infoCenter.news.items.partnership.title'),
    description: t('infoCenter.news.items.partnership.description'),
    image: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.partnership'),
    date: formatRelativeDate(1), // Yesterday
    author: 'Executive Office',
    attachments: [
      {
        name: 'Partnership_Agreement.pdf',
        url: '#'
      }
    ]
  },
  {
    title: t('infoCenter.news.items.sustainability.title'),
    description: t('infoCenter.news.items.sustainability.description'),
    image: 'https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.sustainability'),
    date: formatRelativeDate(2), // 2 days ago
    author: 'Sustainability Team',
    attachments: [
      {
        name: 'Sustainability_Report_Q4.pdf',
        url: '#'
      },
      {
        name: 'Carbon_Footprint_Analysis.xlsx',
        url: '#'
      }
    ]
  },
  {
    title: t('infoCenter.news.items.productLaunch.title'),
    description: t('infoCenter.news.items.productLaunch.description'),
    image: 'https://images.unsplash.com/photo-1551650975-87deedd944c3?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.product'),
    date: formatRelativeDate(3), // 3 days ago
    author: 'Product Development',
    attachments: [
      {
        name: 'Product_Specs.pdf',
        url: '#'
      }
    ]
  },
  {
    title: t('infoCenter.news.items.officeExpansion.title'),
    description: t('infoCenter.news.items.officeExpansion.description'),
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.company'),
    date: formatRelativeDate(4), // 4 days ago
    author: 'Operations',
    attachments: []
  },
  {
    title: t('infoCenter.news.items.training.title'),
    description: t('infoCenter.news.items.training.description'),
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.training'),
    date: formatRelativeDate(5), // 5 days ago
    author: 'Learning & Development',
    attachments: [
      {
        name: 'Training_Schedule.pdf',
        url: '#'
      }
    ]
  },
  {
    title: t('infoCenter.news.items.award.title'),
    description: t('infoCenter.news.items.award.description'),
    image: 'https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.achievement'),
    date: formatRelativeDate(6), // 6 days ago
    author: 'Communications',
    attachments: []
  },
  {
    title: t('infoCenter.news.items.charity.title'),
    description: t('infoCenter.news.items.charity.description'),
    image: 'https://images.unsplash.com/photo-1532629345422-7515f3d16bb6?w=400&h=250&fit=crop',
    category: t('infoCenter.news.categories.community'),
    date: formatRelativeDate(7), // 7 days ago
    author: 'Corporate Social Responsibility',
    attachments: [
      {
        name: 'Charity_Event_Details.pdf',
        url: '#'
      }
    ]
  }
]);

// Upcoming events (all future dates)
const upcomingEvents = computed(() => [
  {
    title: t('infoCenter.events.items.yearEnd.title'),
    date: formatDate(getFutureDate(7)), // 7 days from now
    description: t('infoCenter.events.items.yearEnd.description'),
    startDate: getFutureDate(7).toISOString().split('T')[0] + 'T14:00:00',
    endDate: getFutureDate(7).toISOString().split('T')[0] + 'T16:00:00'
  },
  {
    title: t('infoCenter.events.items.flexibleWork.title'),
    date: formatDate(getFutureDate(12)), // 12 days from now
    description: t('infoCenter.events.items.flexibleWork.description'),
    startDate: getFutureDate(12).toISOString().split('T')[0] + 'T10:00:00',
    endDate: getFutureDate(12).toISOString().split('T')[0] + 'T12:00:00'
  },
  {
    title: t('infoCenter.events.items.recognition.title'),
    date: formatDate(getFutureDate(21)), // 21 days from now
    description: t('infoCenter.events.items.recognition.description'),
    startDate: getFutureDate(21).toISOString().split('T')[0] + 'T15:00:00',
    endDate: getFutureDate(21).toISOString().split('T')[0] + 'T16:30:00'
  },
  {
    title: t('infoCenter.events.items.sustainability.title'),
    date: formatDate(getFutureDate(28)), // 28 days from now
    description: t('infoCenter.events.items.sustainability.description'),
    startDate: getFutureDate(28).toISOString().split('T')[0] + 'T09:00:00',
    endDate: getFutureDate(28).toISOString().split('T')[0] + 'T11:00:00'
  },
  {
    title: t('infoCenter.events.items.holiday.title'),
    date: formatDate(getFutureDate(35)), // 35 days from now
    description: t('infoCenter.events.items.holiday.description'),
    startDate: getFutureDate(35).toISOString().split('T')[0] + 'T18:00:00',
    endDate: getFutureDate(35).toISOString().split('T')[0] + 'T21:00:00'
  },
  {
    title: t('infoCenter.events.items.motivational.title'),
    date: formatDate(getFutureDate(42)), // 42 days from now
    description: t('infoCenter.events.items.motivational.description'),
    startDate: getFutureDate(42).toISOString().split('T')[0] + 'T11:00:00',
    endDate: getFutureDate(42).toISOString().split('T')[0] + 'T12:00:00'
  },
  {
    title: t('infoCenter.events.items.kickoff.title'),
    date: formatDate(getFutureDate(49)), // 49 days from now
    description: t('infoCenter.events.items.kickoff.description'),
    startDate: getFutureDate(49).toISOString().split('T')[0] + 'T13:00:00',
    endDate: getFutureDate(49).toISOString().split('T')[0] + 'T15:00:00'
  },
  {
    title: t('infoCenter.events.items.lunch.title'),
    date: formatDate(getFutureDate(56)), // 56 days from now
    description: t('infoCenter.events.items.lunch.description'),
    startDate: getFutureDate(56).toISOString().split('T')[0] + 'T12:00:00',
    endDate: getFutureDate(56).toISOString().split('T')[0] + 'T13:30:00'
  }
]);

// Calendar integration functions
const addToCalendar = (event, type) => {
  const eventData = {
    title: event.title,
    description: event.description,
    start: event.startDate,
    end: event.endDate,
    location: location.value
  };

  if (type === 'google') {
    const googleUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventData.title)}&dates=${formatGoogleDate(eventData.start)}/${formatGoogleDate(eventData.end)}&details=${encodeURIComponent(eventData.description)}&location=${encodeURIComponent(eventData.location)}`;
    window.open(googleUrl, '_blank');
  } else if (type === 'outlook') {
    const outlookUrl = `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(eventData.title)}&body=${encodeURIComponent(eventData.description)}&startdt=${eventData.start}&enddt=${eventData.end}&location=${encodeURIComponent(eventData.location)}`;
    window.open(outlookUrl, '_blank');
  } else if (type === 'ics') {
    downloadICS(eventData);
  }
};

const formatGoogleDate = (dateString) => {
  return dateString.replace(/[-:]/g, '').split('.')[0] + 'Z';
};

const downloadICS = (eventData) => {
  const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//OneVR//Info Center//EN
BEGIN:VEVENT
UID:${Date.now()}@onevr.com
DTSTAMP:${formatICSDate(new Date())}
DTSTART:${formatICSDate(eventData.start)}
DTEND:${formatICSDate(eventData.end)}
SUMMARY:${eventData.title}
DESCRIPTION:${eventData.description}
LOCATION:${eventData.location}
END:VEVENT
END:VCALENDAR`;

  const blob = new Blob([icsContent], { type: 'text/calendar' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${eventData.title.replace(/\s+/g, '_')}.ics`;
  link.click();
};

const formatICSDate = (date) => {
  const d = new Date(date);
  return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
};

// Force scroll to top before component mounts
onBeforeMount(() => {
  if (typeof window !== 'undefined') {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }
});

// Scroll to top and setup animations on mount
onMounted(() => {
  // Multiple aggressive scroll attempts
  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    
    if (containerRef.value) {
      containerRef.value.scrollTop = 0;
    }
    
    if (marketingSectionRef.value) {
      marketingSectionRef.value.scrollIntoView({ block: 'start', inline: 'nearest', behavior: 'instant' });
    }
  };

  // Immediate scroll
  scrollToTop();
  
  // Scroll after Vue renders
  nextTick(() => {
    scrollToTop();
    
    // Additional safety scroll after a tiny delay
    setTimeout(scrollToTop, 10);
    setTimeout(scrollToTop, 50);
    setTimeout(scrollToTop, 100);
  });

  // Setup intersection observer for scroll animations
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-in');
      }
    });
  }, observerOptions);

  // Observe elements for scroll animations
  setTimeout(() => {
    document.querySelectorAll('.news-card, .event-card, .feature-item').forEach(card => {
      observer.observe(card);
    });
  }, 150);
});
</script>
<style scoped>
.info-center-container {
  min-height: 100vh;
  position: relative;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  overflow-x: hidden;
}

/* Background Effects */
.info-center-bg {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
}

.floating-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.25;
  animation: float 25s infinite ease-in-out;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  top: -250px;
  left: -250px;
  animation-delay: 0s;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  bottom: -200px;
  right: -200px;
  animation-delay: 7s;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  top: 50%;
  left: 50%;
  animation-delay: 14s;
}

.gradient-mesh {
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 20% 30%, rgba(102, 126, 234, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 80% 70%, rgba(240, 147, 251, 0.08) 0%, transparent 50%),
    radial-gradient(circle at 50% 50%, rgba(79, 172, 254, 0.05) 0%, transparent 50%);
}

.light-rays {
  position: absolute;
  inset: 0;
  background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.03) 50%, transparent 70%);
  animation: shimmer 10s infinite linear;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) scale(1) rotate(0deg); }
  25% { transform: translate(40px, -40px) scale(1.1) rotate(90deg); }
  50% { transform: translate(-30px, 30px) scale(0.9) rotate(180deg); }
  75% { transform: translate(30px, 20px) scale(1.05) rotate(270deg); }
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

/* Main Content */
.info-center-content {
  position: relative;
  z-index: 1;
  max-width: 1600px;
  margin: 0 auto;
  padding: 2rem;
}

/* Hero Section */
.info-hero {
  margin-bottom: 2rem;
  animation: fadeInUp 0.6s ease-out;
}

.hero-welcome {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 24px;
  padding: 2.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3);
  color: white;
  position: relative;
  overflow: hidden;
}

.hero-welcome::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: rotate 20s linear infinite;
}

@keyframes rotate {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.avatar-circle {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 3px solid rgba(255, 255, 255, 0.3);
  position: relative;
  z-index: 1;
}

.avatar-circle svg {
  color: white;
}

.welcome-text {
  flex: 1;
  position: relative;
  z-index: 1;
}

.welcome-title {
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 0.75rem 0;
  letter-spacing: -0.5px;
}

.location-info {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  font-size: 0.95rem;
  opacity: 0.95;
}

.location-name {
  font-weight: 500;
}

.weather-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.weather-icon {
  animation: rotate 20s linear infinite;
}

/* Marketing Section */
.marketing-section {
  margin-bottom: 3rem;
  animation: fadeInUp 0.6s ease-out 0.2s backwards;
}

.marketing-container {
  background: white;
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.08);
  text-align: center;
}

.marketing-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
}

.marketing-title {
  font-size: 2.5rem;
  font-weight: 800;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 1rem 0;
  line-height: 1.2;
}

.marketing-description {
  font-size: 1.125rem;
  color: #4a5568;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.7;
}

.features-highlight {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
}

.feature-item {
  display: flex;
  gap: 1rem;
  text-align: left;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(20px);
}

.feature-item.animate-in {
  animation: cardFadeIn 0.6s ease-out forwards;
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(102, 126, 234, 0.15);
}

.feature-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
}

.feature-text h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
}

.feature-text p {
  font-size: 0.9rem;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

/* Grid Layout */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 450px;
  gap: 2rem;
  align-items: start;
}

/* Section Styling */
.info-section {
  background: white;
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.section-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #f1f3f5;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0;
}

.section-subtitle {
  font-size: 0.95rem;
  color: #718096;
  margin: 0;
}

/* News Section */
.news-section {
  animation: fadeInUp 0.6s ease-out 0.3s backwards;
}

.news-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.news-card {
  background: #f8f9fa;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(20px);
  border: 1px solid #e9ecef;
}

.news-card.animate-in {
  animation: cardFadeIn 0.5s ease-out forwards;
}

.news-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.12);
  border-color: #667eea;
}

.news-image {
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  position: relative;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.news-card:hover .news-image img {
  transform: scale(1.08);
}

.news-category {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  color: #667eea;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.news-content {
  padding: 1.5rem;
}

.news-meta {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  font-size: 0.85rem;
  color: #718096;
}

.news-date {
  font-weight: 500;
}

.news-author {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.news-author::before {
  content: '•';
  margin-right: 0.25rem;
}

.news-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.75rem 0;
  line-height: 1.4;
}

.news-description {
  font-size: 0.9rem;
  color: #718096;
  margin: 0 0 1rem 0;
  line-height: 1.6;
}

/* File Attachments */
.news-attachments {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.attachment-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a5568;
  margin-bottom: 0.75rem;
}

.attachment-label svg {
  color: #667eea;
}

.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.attachment-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.85rem;
  color: #667eea;
  text-decoration: none;
  transition: all 0.2s ease;
}

.attachment-link:hover {
  background: #f8f9fa;
  border-color: #667eea;
  transform: translateX(4px);
}

.attachment-link svg {
  flex-shrink: 0;
}

/* Events Section */
.events-section {
  position: sticky;
  top: 2rem;
  animation: fadeInUp 0.6s ease-out 0.4s backwards;
}

.events-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-height: calc(100vh - 250px);
  overflow-y: auto;
  padding-right: 0.5rem;
}

.events-list::-webkit-scrollbar {
  width: 6px;
}

.events-list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.events-list::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 10px;
}

.events-list::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

.event-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  padding: 1.25rem;
  border-left: 4px solid #667eea;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateX(20px);
  border: 1px solid #e9ecef;
  border-left: 4px solid #667eea;
}

.event-card.animate-in {
  animation: cardSlideIn 0.5s ease-out forwards;
}

.event-card:hover {
  transform: translateX(-4px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.15);
  border-color: #667eea;
}

.event-content {
  margin-bottom: 1rem;
}

.event-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
}

.event-date {
  font-size: 0.85rem;
  color: #667eea;
  font-weight: 500;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
}

.event-description {
  font-size: 0.85rem;
  color: #718096;
  margin: 0;
  line-height: 1.5;
}

.event-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.calendar-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: #4a5568;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.calendar-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.google-btn:hover {
  background: #4285f4;
  color: white;
  border-color: #4285f4;
}

.outlook-btn:hover {
  background: #0078d4;
  color: white;
  border-color: #0078d4;
}

.ics-btn:hover {
  background: #667eea;
  color: white;
  border-color: #667eea;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes cardSlideIn {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Responsive Design */
@media (max-width: 1400px) {
  .info-grid {
    grid-template-columns: 1fr 400px;
  }
}

@media (max-width: 1200px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .events-section {
    position: static;
  }

  .events-list {
    max-height: none;
  }
}

@media (max-width: 768px) {
  .info-center-content {
    padding: 1rem;
  }

  .hero-welcome {
    flex-direction: column;
    text-align: center;
    padding: 2rem 1.5rem;
  }

  .welcome-title {
    font-size: 1.5rem;
  }

  .location-info {
    justify-content: center;
    font-size: 0.85rem;
  }

  .marketing-container {
    padding: 2rem 1.5rem;
  }

  .marketing-title {
    font-size: 1.75rem;
  }

  .marketing-description {
    font-size: 1rem;
  }

  .features-highlight {
    grid-template-columns: 1fr;
  }

  .news-grid {
    grid-template-columns: 1fr;
  }

  .section-title {
    font-size: 1.25rem;
  }

  .info-section {
    padding: 1.5rem;
  }
}
</style>